import { FieldError } from "generated/graphql";
import i18n from "locales/i18n";
import { createFormatters } from "locales/format";

// Resolves the language per call, so messages follow the switcher.
const tv = i18n.getFixedT(null, "validation");

const checkRequired = (
  name: string,
  value: string,
  errorMessage?: string,
): string | null => {
  if (!value.trim()) {
    return errorMessage || tv("required", { name });
  }

  return null;
};

type EmailValidationProps = {
  name: string;
  value: string;
  required: boolean;
  errorMessage?: string;
};

const validateEmail = ({
  name,
  value,
  required,
  errorMessage,
}: EmailValidationProps): string | null => {
  if (required && checkRequired(name, value)) {
    return tv("required", { name });
  }

  const emailRegExp = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );

  const validEmail = emailRegExp.test(value);

  if (!validEmail) {
    return errorMessage || tv("invalid", { name });
  }

  return null;
};

type PasswordValidationProps = {
  name: string;
  value: string;
  required?: boolean;
  minLength?: number;
  requireUppercase?: boolean;
  requireLowercase?: boolean;
  requireNumber?: boolean;
  requireSpecialChar?: boolean;
  errorMessage?: string;
};

const validatePassword = ({
  name,
  value,
  required,
  minLength = 8,
  requireUppercase = true,
  requireLowercase = true,
  requireNumber = true,
  requireSpecialChar = false,
}: PasswordValidationProps): string | null => {
  if (required && checkRequired(name, value)) {
    return tv("required", { name });
  }

  if (!value.trim()) {
    return null;
  }

  if (value.length < minLength) {
    return tv("min_chars", { name, count: minLength });
  }

  if (requireUppercase && !/[A-Z]/.test(value)) {
    return tv("uppercase", { name });
  }

  if (requireLowercase && !/[a-z]/.test(value)) {
    return tv("lowercase", { name });
  }

  if (requireNumber && !/\d/.test(value)) {
    return tv("number", { name });
  }

  if (requireSpecialChar && !/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
    return tv("special", { name });
  }

  return null;
};

/* -------------------------------------------------------------------------- */
/*  Phone number — "(123) 456-7890"                                            */
/* -------------------------------------------------------------------------- */

const PHONE_NUMBER_LENGTH = 10;

export const PHONE_NUMBER_PLACEHOLDER = "(123) 456-7890";

/**
 * Masks free typing into "(123) 456-7890". Each separator is appended as soon
 * as the segment before it is complete, so the user carries straight on into
 * the next group of digits.
 */
const formatPhoneNumber = (input: string, previous = ""): string => {
  const previousDigits = previous.replace(/\D/g, "");

  const rawDigits = input.replace(/\D/g, "");

  // A pasted number often carries the North American country code — drop it
  // rather than letting it shunt the real digits out of the mask.
  let digits = (
    rawDigits.length > PHONE_NUMBER_LENGTH && rawDigits.startsWith("1")
      ? rawDigits.slice(1)
      : rawDigits
  ).slice(0, PHONE_NUMBER_LENGTH);

  // A delete that only ate separator characters should still remove a digit.
  if (input.length < previous.length && digits === previousDigits) {
    digits = digits.slice(0, -1);
  }

  const areaCode = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  const line = digits.slice(6);

  if (digits.length < 3) {
    return digits;
  }

  if (digits.length < 6) {
    return `(${areaCode}) ${exchange}`;
  }

  return `(${areaCode}) ${exchange}-${line}`;
};

type PhoneValidationProps = {
  name: string;
  value: string;
  required?: boolean;
  errorMessage?: string;
};

const validatePhoneNumber = ({
  name,
  value,
  required,
  errorMessage,
}: PhoneValidationProps): string | null => {
  if (required && checkRequired(name, value)) {
    return tv("required", { name });
  }

  if (!value.trim()) {
    return null;
  }

  // North American phone number format: (xxx) xxx-xxxx, xxx-xxx-xxxx, xxxxxxxxxx
  const phoneRegExp = new RegExp(
    /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
  );

  const validPhone = phoneRegExp.test(value.replace(/\s/g, ""));

  if (!validPhone) {
    return errorMessage || tv("invalid_phone", { name });
  }

  return null;
};

type WebsiteValidationProps = {
  name: string;
  value: string;
  required: boolean;
  errorMessage?: string;
};

const validateWebsite = ({
  name,
  value,
  required,
  errorMessage,
}: WebsiteValidationProps): string | null => {
  if (required && checkRequired(name, value)) {
    return tv("required", { name });
  }

  // Website URL format: http(s)://example.com or www.example.com
  const websiteRegExp = new RegExp(
    /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)$/,
  );

  const validWebsite = websiteRegExp.test(value);

  if (!validWebsite) {
    return errorMessage || tv("invalid_url", { name });
  }

  return null;
};

type NumberValidationProps = {
  name: string;
  value: string;
  required: boolean;
  min?: number;
  max?: number;
  allowDecimals?: boolean;
  minLength?: number;
  maxLength?: number;
  errorMessage?: string;
};

const validateNumber = ({
  name,
  value,
  required,
  min,
  max,
  minLength,
  maxLength,
  allowDecimals = false,
  errorMessage,
}: NumberValidationProps): string | null => {
  if (required && checkRequired(name, value)) {
    return tv("required", { name });
  }

  if (!value.trim()) {
    return null;
  }

  const numberRegExp = allowDecimals
    ? new RegExp(/^-?\d+(\.\d+)?$/)
    : new RegExp(/^-?\d+$/);

  const isValidNumber = numberRegExp.test(value);

  if (!isValidNumber) {
    return allowDecimals
      ? errorMessage || tv("valid_number", { name })
      : errorMessage || tv("whole_number", { name });
  }

  const numValue = parseFloat(value);

  if (min !== undefined && numValue < min) {
    return tv("min", { name, min });
  }

  if (max !== undefined && numValue > max) {
    return tv("max", { name, max });
  }

  if (minLength !== undefined && value.length < minLength) {
    return errorMessage || tv("min_length", { name, count: minLength });
  }

  if (maxLength !== undefined && value.length > maxLength) {
    return errorMessage || tv("max_length", { name, count: maxLength });
  }

  return null;
};

/* -------------------------------------------------------------------------- */
/*  Business number — "234923782 RT 0001"                                      */
/* -------------------------------------------------------------------------- */

const BUSINESS_NUMBER_PROGRAM = "RT";
const BUSINESS_NUMBER_LENGTH = 9;
const BUSINESS_NUMBER_REFERENCE_LENGTH = 4;

export const BUSINESS_NUMBER_REGEX = /^\d{9} RT \d{4}$/;

export const BUSINESS_NUMBER_PLACEHOLDER = "123456789 RT 0001";

/**
 * Masks free typing into "234923782 RT 0001". The RT program identifier is
 * appended on its own as soon as the 9-digit business number is complete, so
 * the user carries straight on into the 4-digit reference number.
 *
 * `previous` is needed because backspacing over the " RT " separator removes no
 * digits — without it the caret would be stuck and the field un-editable.
 */
const formatBusinessNumber = (input: string, previous = ""): string => {
  const previousDigits = previous.replace(/\D/g, "");

  let digits = input
    .replace(/\D/g, "")
    .slice(0, BUSINESS_NUMBER_LENGTH + BUSINESS_NUMBER_REFERENCE_LENGTH);

  // A delete that only ate separator characters should still remove a digit.
  if (input.length < previous.length && digits === previousDigits) {
    digits = digits.slice(0, -1);
  }

  if (digits.length < BUSINESS_NUMBER_LENGTH) {
    return digits;
  }

  const businessNumber = digits.slice(0, BUSINESS_NUMBER_LENGTH);
  const reference = digits.slice(BUSINESS_NUMBER_LENGTH);

  return `${businessNumber} ${BUSINESS_NUMBER_PROGRAM} ${reference}`;
};

type BusinessNumberValidationProps = {
  name: string;
  value: string;
  required?: boolean;
  errorMessage?: string;
};

const validateBusinessNumber = ({
  name,
  value,
  required,
  errorMessage,
}: BusinessNumberValidationProps): string | null => {
  if (required && checkRequired(name, value)) {
    return tv("required", { name });
  }

  if (!value.trim()) {
    return null;
  }

  if (!BUSINESS_NUMBER_REGEX.test(value.trim())) {
    return (
      errorMessage ||
      tv("business_number", {
        name,
        example: BUSINESS_NUMBER_PLACEHOLDER,
      })
    );
  }

  return null;
};

/* -------------------------------------------------------------------------- */
/*  Electrical license number — "1234567"                                      */
/* -------------------------------------------------------------------------- */

const ELECTRICAL_LICENSE_NUMBER_LENGTH = 7;

export const ELECTRICAL_LICENSE_NUMBER_REGEX = /^\d{7}$/;

export const ELECTRICAL_LICENSE_NUMBER_PLACEHOLDER = "1234567";

// Digits only, capped at the 7 the licence/registration number carries.
const formatElectricalLicenseNumber = (input: string): string =>
  input.replace(/\D/g, "").slice(0, ELECTRICAL_LICENSE_NUMBER_LENGTH);

type ElectricalLicenseNumberValidationProps = {
  name: string;
  value: string;
  required?: boolean;
  errorMessage?: string;
};

const validateElectricalLicenseNumber = ({
  name,
  value,
  required,
  errorMessage,
}: ElectricalLicenseNumberValidationProps): string | null => {
  if (required && checkRequired(name, value)) {
    return tv("required", { name });
  }

  if (!value.trim()) {
    return null;
  }

  if (!ELECTRICAL_LICENSE_NUMBER_REGEX.test(value.trim())) {
    return (
      errorMessage ||
      tv("license_digits", {
        name,
        count: ELECTRICAL_LICENSE_NUMBER_LENGTH,
      })
    );
  }

  return null;
};

type FileValidationProps = {
  name: string;
  file: File | null | undefined;
  required?: boolean;
  allowedTypes?: string[]; // e.g., ['pdf', 'jpg', 'png'] or MIME types ['application/pdf', 'image/jpeg']
  maxSizeMB?: number;
  errorMessage?: string;
};

const validateFile = ({
  name,
  file,
  required,
  allowedTypes,
  maxSizeMB,
  errorMessage,
}: FileValidationProps): string | null => {
  if (required && !file) {
    return errorMessage || tv("required", { name });
  }

  if (!file) {
    return null;
  }

  // Check file type if specified
  if (allowedTypes && allowedTypes.length > 0) {
    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    const isValidType = allowedTypes.some((type) => {
      const normalizedType = type.toLowerCase();
      // Check both extension and MIME type
      return (
        fileName.endsWith(`.${normalizedType}`) ||
        fileType === normalizedType ||
        fileType.startsWith(normalizedType.split("/")[0])
      );
    });

    if (!isValidType) {
      return (
        errorMessage ||
        tv("file_types", { name, types: allowedTypes.join(", ") })
      );
    }
  }

  // Check file size if specified
  if (maxSizeMB !== undefined) {
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > maxSizeMB) {
      return errorMessage || tv("file_size", { name, size: maxSizeMB });
    }
  }

  return null;
};

export const toErrorMap = (errors: FieldError[]) => {
  const errorMap: Record<string, string> = {};

  errors.forEach(({ field, message }) => {
    if (!field || !message) {
      return;
    }

    errorMap[field] = message;
  });

  return errorMap;
};

/** Today as an ISO "YYYY-MM-DD" date in the user's own timezone. */
export const todayIsoDate = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

/** "2027-03-04" → "Mar 4, 2027"; empty stays empty. */
export const formatIsoDate = (date: string | null | undefined): string =>
  date
    ? createFormatters(i18n.language).date(`${date}T00:00:00`, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

type ExpiryDateValidationProps = {
  name: string;
  value: string;
  required?: boolean;
};

// An expiry date that has already passed means the document is out of date.
const validateExpiryDate = ({
  name,
  value,
  required,
}: ExpiryDateValidationProps): string | null => {
  if (!value) {
    return required ? tv("required", { name }) : null;
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return tv("invalid_date", { name });
  }

  if (value < todayIsoDate()) {
    return tv("date_passed", { name });
  }

  return null;
};

export {
  checkRequired,
  validateEmail,
  validatePassword,
  validatePhoneNumber,
  formatPhoneNumber,
  validateWebsite,
  validateNumber,
  validateFile,
  validateBusinessNumber,
  formatBusinessNumber,
  validateElectricalLicenseNumber,
  formatElectricalLicenseNumber,
  validateExpiryDate,
};
